/**
 * Ranking, grouping and counting for the findings every analyzer produces.
 *
 * The analyzers in this layer emit findings in whatever order they happen to
 * walk the files: the manifest reader in line order, the vehicle reader in
 * `<Item>` order, the stream reader in directory order. That order carries no
 * meaning for the person reading the report, and five product surfaces each
 * inventing their own way to make it meaningful is how the same folder ends up
 * with five different opinions about what to look at first.
 *
 * So the ordering lives here, and it is not the obvious one. The prior desktop
 * analyzer sorted on severity alone, and that is wrong twice over. First, a
 * one-key sort leaves every tie in scan order, so re-scanning the same folder
 * after touching an unrelated file shuffles the list and the reader loses their
 * place — a report that will not sit still is a report nobody trusts. Second,
 * severity alone cannot say that a broken police car matters more than a broken
 * sedan. It does: the emergency fleet is what a roleplay server's players see
 * every session, and a siren that does not light up is noticed within minutes
 * while a misspelled handlingId on a tuner car may go unreported for weeks.
 * That is what the emergency-impact tag is for, and why it ranks above
 * everything except severity itself.
 *
 * The rest of this module is the small shared work that used to be duplicated
 * and drift: counting by severity, turning counts into the exact PackBench
 * headline (the pluralisation is on screen, so "1 warnings" is a visible bug),
 * grouping by the entity a finding is about, and collapsing the duplicate
 * findings the desktop analyzer emits because it parses some files twice.
 *
 * Both the ordering and the deduplication compare names and paths through the
 * normalizers instead of as raw text, and that is not tidiness. A manifest
 * declares `data\Vehicles.meta`, the collector that walked the folder lists
 * `data/vehicles.meta`, and the two passes that both notice the same missing
 * texture dictionary each name the file the way they received it. Compared as
 * written, one file is two: the reader gets the same missing `.ytd` on two rows,
 * and one vehicle's problems land in two blocks a page apart, because a capital
 * `V` sorts ahead of every lower-case letter there is.
 *
 * Limits. Nothing here judges a finding: if an analyzer marked a cosmetic
 * problem as an error, this module will faithfully rank it first and block the
 * export. It also knows nothing about which vehicles are emergency vehicles —
 * that decision belongs to the analyzer that had the model name in hand, and
 * reaches this module only as a tag. Everything is pure, allocates a new array
 * rather than sorting in place, and never throws, including on findings whose
 * severity is a string this layer does not define.
 */

import { normalizeName, pathKey } from "./normalize";
import type { GtaEntity, GtaFinding, GtaFindingTag, GtaSeverity, GtaVerdict } from "./types";

// ─── Caps ───────────────────────────────────────────────────────────────────

/**
 * The most findings any function here will consider.
 *
 * A single malformed `vehicles.meta` with a few thousand `<Item>` entries can
 * produce a finding per entry, and the PackBench preflight panel and the desktop
 * report list both render one row per finding. Past this point the list is not
 * readable by a human under any sorting, so it is truncated rather than allowed
 * to lock up the panel. Every function in this module applies the same cut, so
 * the headline, the counts and the visible list always describe the same set of
 * findings instead of disagreeing with each other.
 */
const MAX_FINDINGS_PER_REPORT = 20000;

/** The first `MAX_FINDINGS_PER_REPORT` findings, or the input when it is short. */
function withinCap(findings: readonly GtaFinding[]): readonly GtaFinding[] {
  if (findings.length <= MAX_FINDINGS_PER_REPORT) return findings;
  return findings.slice(0, MAX_FINDINGS_PER_REPORT);
}

// ─── Severity ───────────────────────────────────────────────────────────────

/** Lower sorts first. Errors block an export, warnings do not, info never does. */
export const SEVERITY_RANK: Record<GtaSeverity, number> = {
  error: 0,
  warning: 1,
  info: 2,
};

/**
 * A severity this layer does not define ranks last rather than poisoning the
 * sort. Findings arrive from a desktop build that may be older than this module,
 * and a NaN comparison there would scramble the whole report instead of
 * misplacing one row.
 */
const UNKNOWN_SEVERITY_RANK = 3;

function severityRank(finding: GtaFinding): number {
  const rank = SEVERITY_RANK[finding.severity];
  return typeof rank === "number" ? rank : UNKNOWN_SEVERITY_RANK;
}

/** The tag that lifts a finding within its severity band. */
const EMERGENCY_TAG: GtaFindingTag = "emergency-impact";

/**
 * Whether this finding is about a police, fire, EMS or tow vehicle.
 *
 * The analyzer that recognised the model set the tag; all this does is read it,
 * which is deliberate — the model list belongs to `data/emergencyVehicles.v1.ts`
 * and a second opinion about it here would be a second thing to keep in step.
 */
export function isEmergencyFinding(finding: GtaFinding): boolean {
  return finding.tags?.includes(EMERGENCY_TAG) === true;
}

// ─── Ordering ───────────────────────────────────────────────────────────────

/**
 * Named beats unnamed, and two spellings of one name are one name.
 *
 * A finding that names no entity, no file or no line is about something broader
 * than one, so it sorts after the ones that can point at a place. Sorting it
 * first would put "this resource has no manifest" between two lines of
 * `vehicles.meta` for no reason a reader could explain.
 *
 * The comparison itself is on the normalized key rather than the raw text,
 * because `Police` and `police` are one car and `data\Vehicles.meta` and
 * `data/vehicles.meta` are one file, and a report that disagrees files one
 * vehicle's problems in two places. Two spellings that normalize alike compare
 * equal outright rather than falling back to the raw text: they name the same
 * file, so the *line* is what should decide which comes first, and a raw
 * tie-break here would jump the queue and interleave the lines of one file.
 */
function compareOptionalText(
  a: string | undefined,
  b: string | undefined,
  keyOf: (value: string) => string,
): number {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  const left = keyOf(a);
  const right = keyOf(b);
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

function compareOptionalLine(a: number | undefined, b: number | undefined): number {
  const left = typeof a === "number" && Number.isFinite(a) ? a : undefined;
  const right = typeof b === "number" && Number.isFinite(b) ? b : undefined;
  if (left === right) return 0;
  if (left === undefined) return 1;
  if (right === undefined) return -1;
  return left - right;
}

/** One finding paired with where it arrived, so ties keep the analyzer's order. */
type RankedFinding = { finding: GtaFinding; arrival: number };

/**
 * Severity, then the emergency fleet, then entity, file and line.
 *
 * The arrival index is the last key rather than a hope about the engine: a sort
 * that is stable in one runtime and not another produces reports that differ
 * between the web app and the desktop shell over the same folder, which is the
 * exact class of disagreement this whole layer exists to end.
 */
function compareRanked(a: RankedFinding, b: RankedFinding): number {
  const bySeverity = severityRank(a.finding) - severityRank(b.finding);
  if (bySeverity !== 0) return bySeverity;

  const aEmergency = isEmergencyFinding(a.finding) ? 0 : 1;
  const bEmergency = isEmergencyFinding(b.finding) ? 0 : 1;
  if (aEmergency !== bEmergency) return aEmergency - bEmergency;

  const byEntity = compareOptionalText(a.finding.entity?.id, b.finding.entity?.id, normalizeName);
  if (byEntity !== 0) return byEntity;

  const byFile = compareOptionalText(a.finding.file, b.finding.file, pathKey);
  if (byFile !== 0) return byFile;

  const byLine = compareOptionalLine(a.finding.line, b.finding.line);
  if (byLine !== 0) return byLine;

  return a.arrival - b.arrival;
}

/**
 * The report order every surface renders.
 *
 * Returns a new array; the caller's is untouched, because analyzers hand their
 * own findings array straight to this and a sort in place would reorder the
 * `VehicleEntity.findings` list they still hold a reference to.
 */
export function sortFindings(findings: readonly GtaFinding[]): GtaFinding[] {
  const ranked: RankedFinding[] = [];
  for (const finding of withinCap(findings)) {
    ranked.push({ finding, arrival: ranked.length });
  }
  ranked.sort(compareRanked);
  return ranked.map((entry) => entry.finding);
}

// ─── Counts and verdict ─────────────────────────────────────────────────────

/** How many of each severity, for a badge row that never has to sort anything. */
export function summarizeFindings(findings: readonly GtaFinding[]): {
  error: number;
  warning: number;
  info: number;
} {
  const counts = { error: 0, warning: 0, info: 0 };
  for (const finding of withinCap(findings)) {
    if (finding.severity === "error") counts.error += 1;
    else if (finding.severity === "warning") counts.warning += 1;
    else if (finding.severity === "info") counts.info += 1;
  }
  return counts;
}

/**
 * One error is enough to block, however many warnings sit next to it.
 *
 * The severities are a promise about consequence, not a score: an error means
 * the resource does not do what its author intended once it is on a server, and
 * no number of merely suspicious warnings adds up to that.
 */
export function verdictFor(findings: readonly GtaFinding[]): GtaVerdict {
  let sawWarning = false;
  for (const finding of withinCap(findings)) {
    if (finding.severity === "error") return "blocked";
    if (finding.severity === "warning") sawWarning = true;
  }
  return sawWarning ? "warnings" : "clean";
}

/**
 * The headline PackBench puts above the export button, verbatim.
 *
 * The singular case is the reason this is a function and not a lookup table:
 * one warning is the most common non-clean result there is, and "Export
 * possible, but 1 warnings" is the kind of thing a user screenshots.
 */
export function describeVerdict(findings: readonly GtaFinding[]): string {
  const verdict = verdictFor(findings);
  if (verdict === "blocked") return "Fix required before export";
  if (verdict === "clean") return "Ready to export";
  const warnings = summarizeFindings(findings).warning;
  return `Export possible, but ${warnings} ${warnings === 1 ? "warning" : "warnings"}`;
}

// ─── Grouping ───────────────────────────────────────────────────────────────

/**
 * Findings gathered under the thing they are about, first appearance first.
 *
 * Grouping by entity rather than by file is what turns nine errors into one
 * vehicle whose handlingId is misspelled and the eight things that stop working
 * because of it. Order is first appearance rather than alphabetical so that a
 * caller which already sorted with `sortFindings` keeps that ranking: the worst
 * vehicle stays at the top of the page.
 *
 * The kind is part of the key. A modkit and a vehicle can legitimately share an
 * id, and merging them would file a kit's problems under a car. The id is not,
 * quite: it is matched case-insensitively, because `<modelName>POLICE</modelName>`
 * in one file and `police` in the next are the same vehicle to the game and two
 * headings in a report that compares them as written.
 */
export function groupByEntity(
  findings: readonly GtaFinding[],
): { entity: GtaEntity | null; findings: GtaFinding[] }[] {
  const groups: { entity: GtaEntity | null; findings: GtaFinding[] }[] = [];
  const byKey = new Map<string, { entity: GtaEntity | null; findings: GtaFinding[] }>();
  const unattached: GtaFinding[] = [];

  for (const finding of withinCap(findings)) {
    const entity = finding.entity;
    if (!entity) {
      unattached.push(finding);
      continue;
    }
    const key = `${entity.kind}\u0000${normalizeName(entity.id)}`;
    let group = byKey.get(key);
    if (!group) {
      group = { entity, findings: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.findings.push(finding);
  }

  // The entity-less group trails because it is the report's "everything else":
  // a missing manifest belongs after the vehicles it would have registered.
  if (unattached.length > 0) groups.push({ entity: null, findings: unattached });
  return groups;
}

// ─── Tagging ────────────────────────────────────────────────────────────────

/**
 * The finding with a tag added, as a copy.
 *
 * Analyzers tag late — the vehicle reader produces findings before anything has
 * decided the model is emergency fleet — and the finding being tagged may
 * already sit in a `VehicleEntity.findings` array the caller is holding, so this
 * never writes through. A tag the finding already carries is not added twice; a
 * badge row would otherwise show emergency-impact twice on the same row.
 */
export function withTag(finding: GtaFinding, tag: GtaFindingTag): GtaFinding {
  const tags = finding.tags ?? [];
  if (tags.includes(tag)) return finding;
  return { ...finding, tags: [...tags, tag] };
}

// ─── Duplicates ─────────────────────────────────────────────────────────────

/**
 * The identity `dedupeFindings` treats as one problem, as a value.
 *
 * Exported because a surface that renders findings needs the *same* answer to
 * "is this the same problem" that the deduplicator gives, and the only other way
 * to get it is to rebuild this join by hand. `FindingsList` files each fix
 * outcome under this key so that a click's result lands on the row it was
 * clicked from; with a second copy of the tuple, the day one of the two learned
 * about `detail`, or stopped folding the path, would be the day a refusal
 * appeared under a file nobody touched. A caller that deduped and a caller that
 * did not must not disagree about which row a click belongs to, and that is only
 * guaranteed while there is one rule in one place — the same argument that keeps
 * the ordering here.
 *
 * NUL separates the parts: it cannot occur in a path a file system produced or
 * in a title this layer writes, so no two distinct findings collide on the join.
 *
 * The path is compared through `pathKey` because the two passes that found the
 * same problem rarely spell it alike — one repeats what the manifest declared,
 * `data\Vehicles.meta`, the other what it opened, `data/vehicles.meta`. On the
 * raw strings those are two problems, and the duplicate row `dedupeFindings`
 * exists to remove survives. `pathKey` is plain string work, so a texture called
 * `police+hi.ytd` stays a name and never becomes a pattern that also matches
 * `policehi.ytd`.
 *
 * Deliberately not `detail`: two passes can word the same problem slightly
 * differently, and treating those as two findings defeats the entire point.
 */
export function findingKey(finding: GtaFinding): string {
  return [
    finding.code,
    finding.file === undefined ? "" : pathKey(finding.file),
    finding.line === undefined ? "" : String(finding.line),
    finding.title,
  ].join("\u0000");
}

/**
 * One row per distinct problem, keeping the first occurrence.
 *
 * The desktop analyzer reads several files twice — once while collecting the
 * stream namespace and again while reading vehicle metadata — and reports the
 * same missing `.ytd` from both passes. Deduplicating at the source would mean
 * teaching every pass about every other pass, so it is done here instead, for
 * every producer at once.
 *
 * Identity is code, file, line and title. Deliberately not detail: two passes
 * can word the same problem slightly differently, and treating those as separate
 * findings would defeat the entire point. The first occurrence is the one kept,
 * because it carries the span and the fix from the pass that had the parsed file
 * in hand, which the later duplicate usually does not.
 */
export function dedupeFindings(findings: readonly GtaFinding[]): GtaFinding[] {
  const seen = new Set<string>();
  const unique: GtaFinding[] = [];
  for (const finding of withinCap(findings)) {
    const key = findingKey(finding);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(finding);
  }
  return unique;
}
